import React, { useState, useEffect, useCallback, useRef, type ChangeEvent, type ClipboardEvent } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FlaskConical, Check, X, AlertCircle, Loader2, FileText, Eye, ThumbsUp, ThumbsDown, HelpCircle, ChevronRight, Image as ImageIcon, RotateCcw, ExternalLink, MoreHorizontal, Database, Trash2, Lock, GitBranch, Upload, Plus, ClipboardCopy, CheckCircle2 } from 'lucide-react';
import { api, useAuth } from '@onvice/portal-shell-api';

import { testCockpitApi, type TestRun, type TestFinding, type AggregateFinding, type RunStats } from '../../../lib/testCockpitApi';
import RunStartModal from '../components/RunStartModal';
import TesterSnapshotPanel from '../components/TesterSnapshotPanel';
import MasterFindingsEditor from '../components/MasterFindingsEditor';

/** Wuerfel-Status zu Label + Farbe. */
const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  open:   { label: 'offen',   cls: 'bg-slate-500/15 text-slate-700 dark:text-slate-300' },
  pass:   { label: '✓ passt',  cls: 'bg-brand-500/15 text-brand-700 dark:text-brand-300' },
  fail:   { label: '✗ Fehler', cls: 'bg-rose-500/15 text-rose-700 dark:text-rose-300' },
};

const TRIAGE_BADGE: Record<string, { label: string; cls: string }> = {
  open:    { label: 'untriagiert', cls: 'bg-slate-500/15 text-slate-700 dark:text-slate-300' },
  release: { label: 'freigegeben', cls: 'bg-brand-500/15 text-brand-700 dark:text-brand-300' },
  discard: { label: 'verworfen',   cls: 'bg-rose-500/15 text-rose-700 dark:text-rose-300' },
  clarify: { label: 'klären',      cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
};

/**
 * Knopf, der die richtige Persona setzt und dann den Pfad in einem neuen Tab oeffnet.
 * Cockpit bleibt im aktuellen Tab — Tester wechselt zum neuen Tab, prueft, kommt zurueck.
 */
function TestPathBlock({ testPath }: { testPath: string }) {
  const { persona, url, hint } = parseTestPath(testPath);
  const [busy, setBusy] = useState(false);
  const { persona: currentPersona } = useAuth();

  async function open() {
    if (!url) return;
    // Tab SOFORT oeffnen, solange wir noch im User-Click-Kontext sind — sonst greift der Popup-Blocker
    // und der spaetere window.open (nach await) wird stumm verworfen.
    const tab = window.open('about:blank', '_blank');
    if (!tab) {
      alert('Popup-Blocker hat das Oeffnen verhindert. Bitte Popups fuer staging-probst.onvice.ai erlauben.');
      return;
    }
    setBusy(true);
    try {
      // Persona-Cookie setzen, damit der NEUE Tab unter dieser Persona laedt.
      // Wichtig: KEIN refreshMe() im Cockpit-Tab — sonst wechselt der Auth-Context
      // hier mit und der Cockpit-Header zeigt die Persona statt des realen Testers
      // (Funde #1+#2 vom 2026-05-19: "Tester darf sich im Testcockpit nicht aendern"
      // + "Test-Cockpit-Ansicht aendert sich bei Klick auf Test-Pfad").
      // Cockpit-Backend-Routes nutzen ohnehin realUserId() und ignorieren die Persona.
      if (persona && currentPersona.personaKey !== persona) {
        await api.post(`/test-cockpit/impersonate/${persona}`, {});
      }
      tab.location.href = `${window.location.origin}${url}`;
    } catch (err) {
      console.error('Pfad oeffnen fehlgeschlagen:', err);
      tab.close();
      alert('Persona-Wechsel fehlgeschlagen — Tab wurde geschlossen. Console fuer Details.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="text-xs text-muted-foreground mb-1">Test-Pfad</div>
      {url ? (
        <div className="space-y-2">
          <button
            onClick={open}
            disabled={busy}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brand-500/10 hover:bg-brand-500/20 text-brand-700 dark:text-brand-300 text-sm font-medium border border-brand-500/30 disabled:opacity-50"
            title={persona ? `Wechselt zu tester-${persona} und oeffnet ${url} in neuem Tab` : `Oeffnet ${url} in neuem Tab`}
          >
            <ExternalLink size={16} />
            <span className="font-mono">{url}</span>
            {persona && <span className="text-xs opacity-80">als tester-{persona}</span>}
          </button>
          {hint && <div className="text-sm whitespace-pre-wrap text-muted-foreground">{hint}</div>}
        </div>
      ) : (
        <div className="font-mono text-xs whitespace-pre-wrap">{testPath}</div>
      )}
    </div>
  );
}

/** Parsed test_path-Format `[persona:handwerker] /m/plantafel — Hinweis...` */
function parseTestPath(text: string | null): { persona: string | null; url: string | null; hint: string } {
  if (!text) return { persona: null, url: null, hint: '' };
  let rest = text;
  let persona: string | null = null;
  const personaMatch = rest.match(/^\s*\[persona:([a-z]+)\]\s*/);
  if (personaMatch) {
    persona = personaMatch[1];
    rest = rest.slice(personaMatch[0].length);
  }
  // URL: erste /... bis Whitespace ODER em-dash
  const urlMatch = rest.match(/^(\/\S+?)(?=\s|—|$)/);
  let url: string | null = null;
  if (urlMatch) {
    url = urlMatch[1];
    rest = rest.slice(urlMatch[0].length).trim();
  }
  return { persona, url, hint: rest };
}

export default function TestCockpitPage() {
  const { runId: paramRunId } = useParams<{ runId?: string }>();
  const navigate = useNavigate();
  const { hasPermission, persona, user, loading: authLoading } = useAuth();
  const isAdmin = hasPermission('tests.admin');
  const isTester = hasPermission('tests.run') || (persona.active && persona.realHasTestsRun);

  const [runs, setRuns] = useState<TestRun[]>([]);
  const [loadingRuns, setLoadingRuns] = useState(true);

  useEffect(() => {
    testCockpitApi.listRuns()
      .then(rs => {
        setRuns(rs);
        if (!paramRunId && rs.length > 0) navigate(`/test-cockpit/${rs[0].id}`, { replace: true });
      })
      .catch(() => setRuns([]))
      .finally(() => setLoadingRuns(false));
  }, [paramRunId, navigate]);

  const reloadRuns = useCallback(() => {
    testCockpitApi.listRuns().then(setRuns).catch(() => {});
  }, []);

  const [tab, setTab] = useState<'runs' | 'findings'>(paramRunId ? 'runs' : 'runs');

  if (authLoading || !user || loadingRuns) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand-500" /></div>;
  }

  if (!isAdmin && !isTester) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-muted-foreground space-y-2">
        <div>Du hast keinen Zugriff auf das Test-Cockpit.</div>
        <div className="text-xs">
          Erforderlich: Permission <code>tests.run</code> (Tester) oder <code>tests.admin</code> (Test-Admin).
        </div>
        <div className="text-xs">
          Aktueller User: <code>{user.email}</code> ({user.role}) — Permissions: <code>{(user.permissions ?? []).join(', ') || '(keine)'}</code>
        </div>
        {persona.active && (
          <div className="text-xs">
            Persona aktiv: <code>{persona.personaKey}</code> — falls dies eine alte Sitzung ist, einmal abmelden + neu einloggen raeumt den Persona-Cookie.
          </div>
        )}
      </div>
    );
  }

  const currentRun = runs.find(r => r.id === paramRunId);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <header className="flex items-center justify-between mb-6 gap-3">
        <div className="flex items-center gap-3">
          <FlaskConical className="text-brand-500" />
          <h1 className="text-2xl font-semibold">Test-Cockpit</h1>
          {persona.active && (
            <span className="text-xs px-2 py-1 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300">
              Persona aktiv: {persona.personaKey}
            </span>
          )}
        </div>
        {isAdmin && tab === 'runs' && <RunActionsMenu runId={paramRunId ?? null} run={currentRun} onChanged={reloadRuns} />}
      </header>

      {isAdmin && (
        <div className="flex gap-2 mb-4 text-sm border-b border-default">
          <button
            onClick={() => setTab('runs')}
            className={`px-3 py-2 -mb-px border-b-2 ${tab === 'runs' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            Test-Runs
          </button>
          <button
            onClick={() => setTab('findings')}
            className={`px-3 py-2 -mb-px border-b-2 ${tab === 'findings' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            Findings (Master)
          </button>
        </div>
      )}

      {isAdmin && tab === 'findings' && <MasterFindingsEditor />}

      {tab === 'runs' && (
        <>
          {/* Run-Anzeige: Admin sieht Dropdown (mit Verlauf), Tester sieht nur Run-Name read-only. */}
          <div className="mb-6">
            {runs.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                {isAdmin ? 'Noch kein Test-Run angelegt.' : 'Kein aktiver Test-Run — der Test-Admin legt einen an, sobald die Welle startet.'}
              </div>
            ) : isAdmin ? (
              <>
                <label className="text-sm text-muted-foreground block mb-1">Aktueller Test-Run</label>
                <select
                  className="w-full max-w-md px-3 py-2 rounded-lg bg-card border border-default text-sm"
                  value={paramRunId ?? ''}
                  onChange={(e) => navigate(`/test-cockpit/${e.target.value}`)}
                >
                  {runs.map(r => (
                    <option key={r.id} value={r.id}>
                      {r.name} — {new Date(r.started_at).toLocaleString('de-DE')}{r.status === 'closed' ? ' (geschlossen)' : ''}
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <div>
                <div className="text-xs text-muted-foreground mb-1">Aktiver Test-Run</div>
                <div className="text-base font-medium">
                  {currentRun?.name ?? runs[0]?.name ?? '—'}
                </div>
                {currentRun && <div className="text-xs text-muted-foreground">gestartet {new Date(currentRun.started_at).toLocaleString('de-DE')}</div>}
              </div>
            )}
          </div>

          {paramRunId && (
            isAdmin
              ? <AdminAggregateView runId={paramRunId} run={currentRun} />
              : <TesterFindingsView runId={paramRunId} run={currentRun ?? runs[0]} />
          )}
        </>
      )}
    </div>
  );
}

// ============================================================================
// Tester-Sicht: eigene Findings + Status + Kommentar + Screenshot
// ============================================================================

function TesterFindingsView({ runId, run }: { runId: string; run: TestRun | undefined }) {
  const [findings, setFindings] = useState<TestFinding[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'open' | 'done'>('open');
  const [showReport, setShowReport] = useState(false);
  const [showChangelog, setShowChangelog] = useState(true);

  // Silent-Reload-Pattern — keinen Loading-Spinner auf bereits geladene Daten setzen.
  // Memory: feedback_status_refresh_silent.md
  const reload = useCallback((silent = false) => {
    if (!silent) setInitialLoading(true);
    testCockpitApi.listFindings(runId)
      .then(setFindings)
      .catch(() => { if (!silent) setFindings([]); })
      .finally(() => { if (!silent) setInitialLoading(false); });
  }, [runId]);

  useEffect(() => { reload(); }, [reload]);
  const reloadSilent = useCallback(() => reload(true), [reload]);

  const filtered = findings.filter(f => {
    if (filter === 'all') return true;
    const s = f.my_status ?? 'open';
    if (filter === 'open') return s === 'open';
    if (filter === 'done') return s !== 'open';
    return true;
  });

  const counts = {
    all: findings.length,
    open: findings.filter(f => (f.my_status ?? 'open') === 'open').length,
    done: findings.filter(f => (f.my_status ?? 'open') !== 'open').length,
  };

  if (initialLoading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand-500" /></div>;

  return (
    <div>
      {run?.tester_changelog && (
        <div className="mb-4 bg-brand-500/5 border border-brand-500/30 rounded-lg">
          <button
            onClick={() => setShowChangelog(s => !s)}
            className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium hover:bg-brand-500/10"
          >
            <span className="flex items-center gap-2">
              <FileText size={14} className="text-brand-500" />
              Was ist neu im Test
            </span>
            <ChevronRight size={14} className={`transition-transform ${showChangelog ? 'rotate-90' : ''}`} />
          </button>
          {showChangelog && (
            <div className="px-4 pb-3 pt-2 border-t border-brand-500/20">
              <MarkdownView source={run.tester_changelog} />
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between gap-2 mb-4 text-sm flex-wrap">
        <div className="flex gap-2">
          {(['open', 'all', 'done'] as const).map(k => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`px-3 py-1.5 rounded-lg ${filter === k ? 'bg-primary/15 text-primary' : 'bg-card border border-default text-muted-foreground hover:text-foreground'}`}
            >
              {k === 'open' ? `Offen (${counts.open})` : k === 'all' ? `Alle (${counts.all})` : `Erledigt (${counts.done})`}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowReport(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-sm font-medium"
          title="Neuen Fehler melden, der noch nicht im Run steht"
        >
          <Plus size={14} /> Neuen Fehler melden
        </button>
      </div>

      <div className="space-y-3">
        {filtered.map(f => (
          <TesterFindingCard key={f.id} finding={f} onChange={reloadSilent} />
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-12 text-muted-foreground text-sm">Keine Findings in dieser Sicht.</div>
        )}
      </div>

      {showReport && (
        <NewTesterFindingModal
          runId={runId}
          onClose={() => setShowReport(false)}
          onCreated={() => { setShowReport(false); reloadSilent(); }}
        />
      )}
    </div>
  );
}

function NewTesterFindingModal({ runId, onClose, onCreated }: { runId: string; onClose: () => void; onCreated: () => void }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [adminComment, setAdminComment] = useState('');
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onPaste(e: ClipboardEvent<HTMLTextAreaElement>) {
    const items = Array.from(e.clipboardData.items).filter(i => i.type.startsWith('image/'));
    if (items.length === 0) return;
    e.preventDefault();
    const file = items[0].getAsFile();
    if (!file) return;
    setBusy(true);
    try {
      const path = await testCockpitApi.uploadScreenshot(file);
      setScreenshot(path);
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    setError(null);
    if (!title.trim()) { setError('Titel erforderlich'); return; }
    if (!description.trim()) { setError('Fehlerbeschreibung erforderlich'); return; }
    setBusy(true);
    try {
      await testCockpitApi.reportTesterFinding(runId, {
        title: title.trim(),
        description: description.trim(),
        admin_comment: adminComment.trim() || undefined,
        screenshot_path: screenshot,
      });
      onCreated();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4">
      <div className="bg-card border border-default rounded-lg shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-default flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <AlertCircle size={18} className="text-rose-500" /> Neuen Fehler melden
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <div className="text-xs text-muted-foreground mb-1">Kurztitel</div>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="z.B. „Plantafel: Drag-and-Drop friert ein"
              className="w-full px-3 py-2 rounded bg-background border border-default text-sm"
            />
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Fehlerbeschreibung (Anweisung an Claude) — Strg+V fuer Screenshot</div>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              onPaste={onPaste}
              rows={4}
              placeholder="Was hast du gesehen? Was sollte stattdessen passieren? Welche Schritte fuehren dazu?"
              className="w-full px-3 py-2 rounded bg-background border border-default text-sm"
            />
            {screenshot && (
              <div className="mt-2 flex items-start gap-2">
                <a href={screenshot} target="_blank" rel="noopener noreferrer">
                  <img src={screenshot} alt="Screenshot" className="max-h-40 rounded border border-default" />
                </a>
                <button onClick={() => setScreenshot(null)} className="text-xs text-muted-foreground hover:text-rose-500">Entfernen</button>
              </div>
            )}
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Hinweis fuer Testadmin (optional, privat)</div>
            <textarea
              value={adminComment}
              onChange={e => setAdminComment(e.target.value)}
              rows={2}
              placeholder="Reproduktionsdetails / Vermutungen — geht nicht an Claude."
              className="w-full px-3 py-2 rounded bg-background border border-default text-sm"
            />
          </div>
          {error && (
            <div className="text-sm text-rose-500 bg-rose-500/10 border border-rose-500/30 rounded p-3">{error}</div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-default flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-2 rounded text-sm hover:bg-primary/5">Abbrechen</button>
          <button
            onClick={submit}
            disabled={busy || !title.trim() || !description.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded bg-rose-500 hover:bg-rose-600 text-white text-sm font-medium disabled:opacity-50"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Fehler melden
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Tester-Karte: zwei Felder + zwei Knoepfe.
 * - Feld 1 ("Fehlerbeschreibung"): Anweisung an Claude oder „passt". Pflichtfeld zum Speichern.
 *   Screenshot via Strg+V ins Feld einfuegen — Bild liegt darunter.
 * - Feld 2 ("Hinweis fuer Testadmin"): privater Kommentar an Joerg.
 * - Knopf „Passt": setzt Feld 1 auf „passt".
 * - Knopf „Passt nicht" (Moritz-Fund 2026-08-27): fuehrt in das
 *   Beschreibungsfeld — neue Tester haben nicht erraten, dass „Fehler
 *   melden" = Text eintippen + Speichern ist. Der Knopf setzt keinen
 *   Status selbst; der wird weiterhin aus dem Text abgeleitet.
 * - Knopf „Speichern": persistiert beide Felder + leitet Status ab (passt → pass, sonst fail).
 */
function TesterFindingCard({ finding, onChange }: { finding: TestFinding; onChange: () => void }) {
  const [description, setDescription] = useState(finding.my_comment ?? '');
  const [adminComment, setAdminComment] = useState(finding.my_admin_comment ?? '');
  const [screenshot, setScreenshot] = useState<string | null>(finding.my_screenshot ?? null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [savedToast, setSavedToast] = useState(false);
  const [failHint, setFailHint] = useState(false);
  const descRef = useRef<HTMLTextAreaElement>(null);

  // Server-Sync NUR bei Finding-Wechsel (id), nicht bei jedem Inhalts-Refresh.
  // Tester (Kiara 2026-05-15): "eingetragene Kommentare werden manchmal
  // verändert" — Wurzel war useEffect([finding.my_comment]), das nach jedem
  // reloadSilent() den lokalen Tippzustand mit Server-Daten ueberschrieb.
  useEffect(() => {
    setDescription(finding.my_comment ?? '');
    setAdminComment(finding.my_admin_comment ?? '');
    setScreenshot(finding.my_screenshot ?? null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finding.id]);

  const status = (finding.my_status as 'open' | 'pass' | 'fail' | null) ?? 'open';
  const dirty = description !== (finding.my_comment ?? '') || adminComment !== (finding.my_admin_comment ?? '');

  function applyPasst() {
    setDescription('passt');
    setFailHint(false);
  }

  function applyPasstNicht() {
    if (description.trim().toLowerCase() === 'passt') setDescription('');
    setFailHint(true);
    descRef.current?.focus();
  }

  async function save() {
    if (!description.trim()) {
      alert('Bitte Fehlerbeschreibung ausfuellen oder „Passt" klicken.');
      return;
    }
    const derivedStatus: 'pass' | 'fail' = description.trim().toLowerCase() === 'passt' ? 'pass' : 'fail';
    setBusy(true);
    try {
      await testCockpitApi.putResult(finding.id, {
        status: derivedStatus,
        comment: description,
        admin_comment: adminComment,
        screenshot_path: screenshot,
      });
      setSavedToast(true);
      setTimeout(() => setSavedToast(false), 2000);
      onChange();
    } catch (err) {
      console.error('[test-cockpit] Speichern fehlgeschlagen:', err);
      alert(`Speichern fehlgeschlagen: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function onPaste(e: ClipboardEvent<HTMLTextAreaElement>) {
    const items = Array.from(e.clipboardData.items).filter(i => i.type.startsWith('image/'));
    if (items.length === 0) return;
    e.preventDefault();
    const file = items[0].getAsFile();
    if (!file) return;
    setBusy(true);
    try {
      const path = await testCockpitApi.uploadScreenshot(file);
      setScreenshot(path);
    } finally {
      setBusy(false);
    }
  }

  const badge = STATUS_BADGE[status];

  return (
    <div className={`bg-card border rounded-lg ${status === 'open' ? 'border-default' : 'border-default/40 opacity-80'}`}>
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-primary/5"
      >
        <ChevronRight size={16} className={`mt-0.5 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        <span className={`text-xs px-2 py-0.5 rounded shrink-0 ${badge.cls}`}>{badge.label}</span>
        <div className="flex-1 min-w-0">
          <div className="font-medium">{finding.title}</div>
          <div className="text-xs text-muted-foreground mt-0.5 truncate">
            {finding.source_file.replace(/^.*\//, '')} · #{finding.idx + 1}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-default">
          <div>
            <div className="text-xs text-muted-foreground mb-1 mt-3">Funktion / Erwartung</div>
            <div className="whitespace-pre-wrap text-sm bg-background/40 rounded p-3">{finding.feature_text}</div>
          </div>
          {finding.expectation && (
            <div>
              <div className="text-xs text-muted-foreground mb-1">Erwartetes Verhalten</div>
              <div className="whitespace-pre-wrap text-sm">{finding.expectation}</div>
            </div>
          )}
          {finding.test_path && <TestPathBlock testPath={finding.test_path} />}

          {/* Feld 1: Fehlerbeschreibung */}
          <div>
            <div className="text-xs text-muted-foreground mb-1">
              Fehlerbeschreibung (eindeutige Anweisung an Claude — oder Knopf „Passt") · Strg+V fuer Screenshot
            </div>
            <textarea
              ref={descRef}
              value={description}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
              onPaste={onPaste}
              rows={3}
              placeholder='Was muss Claude reparieren? Schreibe kurz und eindeutig — z.B. „Beim Klick auf Speichern kommt 500-Fehler.“'
              className={`w-full px-3 py-2 rounded-lg bg-background border text-sm ${failHint && !description.trim() ? 'border-rose-500/60 ring-1 ring-rose-500/30' : 'border-default'}`}
            />
            {failHint && !description.trim() && (
              <div className="mt-1 text-xs text-rose-500">
                Beschreibe kurz, was falsch ist — mit „Speichern" wird der Fund als Fehler gemeldet.
              </div>
            )}
            {screenshot && (
              <div className="mt-2 flex items-start gap-2">
                <a href={screenshot} target="_blank" rel="noopener noreferrer" className="block">
                  <img src={screenshot} alt="Screenshot" className="max-h-40 rounded border border-default" />
                </a>
                <button
                  onClick={() => setScreenshot(null)}
                  className="text-xs text-muted-foreground hover:text-rose-500"
                >Entfernen</button>
              </div>
            )}
          </div>

          {/* Feld 2: Hinweis fuer Testadmin */}
          <div>
            <div className="text-xs text-muted-foreground mb-1">Hinweis fuer Testadmin (privat — geht nicht an Claude)</div>
            <textarea
              value={adminComment}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setAdminComment(e.target.value)}
              rows={2}
              placeholder="Optional: Hinweis fuer Joerg, z.B. Reproduktionsdetails oder Vermutungen."
              className="w-full px-3 py-2 rounded-lg bg-background border border-default text-sm"
            />
          </div>

          {/* Knoepfe */}
          <div className="flex items-center gap-2 pt-2">
            <button
              onClick={applyPasst}
              disabled={busy}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-600 text-primary-foreground text-sm font-medium disabled:opacity-50"
              title='Schreibt „passt“ in das Feld Fehlerbeschreibung — danach Speichern.'
            >
              <Check size={16} /> Passt
            </button>
            <button
              onClick={applyPasstNicht}
              disabled={busy}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-sm font-medium disabled:opacity-50"
              title='Fehler melden: fuehrt in das Beschreibungsfeld — kurz beschreiben, dann Speichern.'
            >
              <X size={16} /> Passt nicht
            </button>
            <button
              onClick={save}
              disabled={busy || !dirty}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium ${dirty ? 'bg-primary text-white hover:bg-primary/90' : 'bg-card border border-default text-muted-foreground'} disabled:opacity-50`}
            >
              {busy && <Loader2 size={14} className="animate-spin" />}
              Speichern
            </button>
            {savedToast && <span className="text-xs text-brand-500">✓ gespeichert</span>}
            {dirty && !savedToast && <span className="text-xs text-amber-500">ungespeicherte Änderungen</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Admin-Sicht: Aggregat aller Tester-Results pro Finding + Triage
// ============================================================================

function AdminAggregateView({ runId, run }: { runId: string; run: TestRun | undefined }) {
  const [findings, setFindings] = useState<AggregateFinding[]>([]);
  const [stats, setStats] = useState<RunStats | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'untriaged' | 'conflicts'>('untriaged');

  const reload = useCallback((silent = false) => {
    if (!silent) setInitialLoading(true);
    Promise.all([
      testCockpitApi.aggregate(runId),
      testCockpitApi.stats(runId).catch(() => null),
    ])
      .then(([f, s]) => { setFindings(f); setStats(s); })
      .catch(() => { if (!silent) { setFindings([]); setStats(null); } })
      .finally(() => { if (!silent) setInitialLoading(false); });
  }, [runId]);

  useEffect(() => { reload(); }, [reload]);
  const reloadSilent = useCallback(() => reload(true), [reload]);

  const filtered = findings.filter(f => {
    if (filter === 'all') return true;
    // "Untriagiert" = alles, was noch keine Endentscheidung hat — open UND clarify gehoeren dazu,
    // sonst verschwindet ein Finding nach Klick auf "Klaeren" sofort aus der Liste und der
    // Admin kann es ohne Filter-Wechsel nicht mehr auf Freigeben/Verwerfen umstellen.
    if (filter === 'untriaged') return f.triage_status === 'open' || f.triage_status === 'clarify';
    if (filter === 'conflicts') {
      const statuses = new Set(f.results.map(r => r.status));
      return statuses.has('pass') && statuses.has('fail');
    }
    return true;
  });

  if (initialLoading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand-500" /></div>;

  return (
    <div>
      {stats && <RunStatsHeader stats={stats} run={run} />}

      <div className="mb-4">
        <TesterSnapshotPanel />
      </div>

      <div className="flex gap-2 mb-4 text-sm">
        {(['untriaged', 'conflicts', 'all'] as const).map(k => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-lg ${filter === k ? 'bg-primary/15 text-primary' : 'bg-card border border-default text-muted-foreground hover:text-foreground'}`}
          >
            {k === 'untriaged' ? 'Untriagiert' : k === 'conflicts' ? 'Tester-Konflikt' : 'Alle'}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.map(f => <AdminFindingCard key={f.id} finding={f} onChange={reloadSilent} />)}
        {filtered.length === 0 && <div className="text-center py-12 text-muted-foreground text-sm">Keine Findings in dieser Sicht.</div>}
      </div>
    </div>
  );
}

// ============================================================================
// Run-Klon-Modal — explizite Anzeige des Quell-Runs + Filter + Name
// ============================================================================

function RunCloneModal({ sourceRun, onClose, onClone }: {
  sourceRun: TestRun;
  onClose: () => void;
  onClone: (filter: 'all' | 'open' | 'fail' | 'unreleased', name: string) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [filter, setFilter] = useState<'all' | 'open' | 'fail' | 'unreleased'>('unreleased');
  const FILTER_LABEL: Record<typeof filter, string> = {
    'all':         'alle Findings',
    'unreleased':  'noch nicht freigegebene (kein release/discard)',
    'fail':        'nur Funde mit Tester-Fehler',
    'open':        'nur untriagierte (noch keine Joerg-Triage)',
  };
  const [name, setName] = useState(`Re-Test ${today}`);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4">
      <div className="bg-card border border-default rounded-lg shadow-xl w-full max-w-xl">
        <div className="px-5 py-4 border-b border-default flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <GitBranch size={18} /> Run klonen
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <div className="text-xs text-muted-foreground mb-1">Quell-Run</div>
            <div className="bg-background/40 border border-default rounded p-3 text-sm">
              <div className="font-medium">{sourceRun.name}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                gestartet {new Date(sourceRun.started_at).toLocaleString('de-DE')} · Status: {sourceRun.status}
              </div>
            </div>
          </div>

          <div>
            <div className="text-xs text-muted-foreground mb-1">Welche Findings uebernehmen?</div>
            <div className="space-y-1">
              {(Object.keys(FILTER_LABEL) as Array<keyof typeof FILTER_LABEL>).map(k => (
                <label key={k} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-primary/5 px-2 py-1 rounded">
                  <input
                    type="radio"
                    name="clone-filter"
                    checked={filter === k}
                    onChange={() => setFilter(k)}
                  />
                  <span>{FILTER_LABEL[k]}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs text-muted-foreground mb-1">Name fuer den neuen Run</div>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full px-3 py-2 rounded bg-background border border-default text-sm"
            />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-default flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 rounded text-sm hover:bg-primary/5">Abbrechen</button>
          <button
            onClick={() => onClone(filter, name.trim() || `Re-Test ${today}`)}
            disabled={!name.trim()}
            className="px-4 py-2 rounded bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
          >
            Klonen
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Run-Stats-Header — Zahlen + Per-Tester-Aufstellung (Admin-Sicht)
// ============================================================================

function RunStatsHeader({ stats, run }: { stats: RunStats; run: TestRun | undefined }) {
  const { totals, consensus, per_tester } = stats;
  const isClosed = run?.status === 'closed';
  return (
    <div className="bg-card border border-default rounded-lg p-4 mb-4 space-y-3">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        <span className="font-medium">Stand:</span>
        <span className="flex items-center gap-1.5"><span className="text-brand-500 font-semibold">✓ {consensus.all_pass}</span><span className="text-muted-foreground">alle gruen</span></span>
        <span className="flex items-center gap-1.5"><span className="text-rose-500 font-semibold">✗ {consensus.any_fail}</span><span className="text-muted-foreground">mind. ein fail</span></span>
        <span className="flex items-center gap-1.5"><span className="text-amber-500 font-semibold">{consensus.partial}</span><span className="text-muted-foreground">teilweise</span></span>
        <span className="flex items-center gap-1.5"><span className="text-slate-500 font-semibold">{consensus.no_results}</span><span className="text-muted-foreground">ohne Feedback</span></span>
        <span className="text-muted-foreground">·</span>
        <span className="text-muted-foreground">Triage:</span>
        <span><span className="font-semibold">{totals.untriaged}</span> untriagiert</span>
        <span><span className="font-semibold text-brand-500">{totals.released}</span> freigegeben</span>
        <span><span className="font-semibold text-rose-500">{totals.discarded}</span> verworfen</span>
        <span><span className="font-semibold text-amber-500">{totals.clarify}</span> klären</span>
        <span className="ml-auto text-muted-foreground">Total: {totals.total}{isClosed && <span className="ml-2 px-2 py-0.5 rounded bg-slate-500/15 text-slate-700 dark:text-slate-300 text-xs">geschlossen</span>}</span>
      </div>
      {per_tester.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground border-t border-default/50 pt-2">
          <span className="font-medium">Pro Tester:</span>
          {per_tester.map(t => (
            <span key={t.tester_id}>
              {t.tester_name || t.tester_email}:{' '}
              <span className="text-brand-500 font-semibold">✓ {t.pass}</span>{' / '}
              <span className="text-rose-500 font-semibold">✗ {t.fail}</span>
              {t.skip > 0 && <>{' / '}<span className="text-amber-500 font-semibold">— {t.skip}</span></>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Run-Actions-Menu — Run schliessen, klonen, Bundle reseed, Screenshots cleanup
// ============================================================================

function RunActionsMenu({ runId, run, onChanged }: { runId: string | null; run: TestRun | undefined; onChanged: () => void }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [showStartModal, setShowStartModal] = useState(false);
  const isClosed = run?.status === 'closed';
  const hasRun = !!runId;

  function flash(msg: string) {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3500);
  }

  async function endRun() {
    if (!runId) return;
    if (!confirm('Run beenden? Tester werden gesperrt und das Test-Bundle wird aus der Probst-DB entfernt.')) return;
    setBusy('end'); setOpen(false);
    try {
      const r = await testCockpitApi.endRun(runId);
      const errs = r.teardown_errors;
      flash(errs.length === 0 ? 'Run beendet, Testdaten entfernt' : `Run beendet, Teardown-Fehler: ${errs.length}`);
      onChanged();
    } catch (e) { alert(`Beenden fehlgeschlagen: ${(e as Error).message}`); }
    finally { setBusy(null); }
  }

  const [showCloneModal, setShowCloneModal] = useState(false);

  async function doClone(filter: 'all' | 'open' | 'fail' | 'unreleased', name: string) {
    if (!runId) return;
    setBusy('clone'); setShowCloneModal(false);
    try {
      const r = await testCockpitApi.cloneRun(runId, { name, only: filter });
      flash(`Neuer Run: ${r.finding_count} Findings`);
      navigate(`/test-cockpit/${r.id}`);
      onChanged();
    } catch (e) { alert(`Klonen fehlgeschlagen: ${(e as Error).message}`); }
    finally { setBusy(null); }
  }

  async function resetBundle() {
    if (!confirm('Test-Bundle in der Probst-DB zuruecksetzen? Alle TEST-Datensaetze werden geloescht und neu eingespielt.')) return;
    setBusy('bundle'); setOpen(false);
    try {
      const r = await testCockpitApi.bundleReset();
      const summary = r.counts.map(c => `${c.typ}: ${c.n}`).join(', ');
      flash(`Bundle reseeded — ${summary}`);
    } catch (e) { alert(`Bundle-Reset fehlgeschlagen: ${(e as Error).message}`); }
    finally { setBusy(null); }
  }

  async function generateChangelog() {
    if (!runId) return;
    if (run?.tester_changelog && !confirm('Es ist bereits ein Changelog gesetzt. Ueberschreiben mit auto-generiertem?')) return;
    setBusy('changelog'); setOpen(false);
    try {
      const r = await testCockpitApi.generateChangelog(runId);
      if (!r.tester_changelog) {
        flash(r.message ?? 'Nichts zu generieren');
      } else {
        flash(`Changelog generiert (${r.finding_count} Funde) — pruefen`);
      }
      onChanged();
    } catch (e) { alert(`Generieren fehlgeschlagen: ${(e as Error).message}`); }
    finally { setBusy(null); }
  }
  async function editChangelog() {
    if (!runId) return;
    const current = run?.tester_changelog ?? '';
    const next = prompt('Tester-Changelog (erscheint im Tester-Cockpit oben). Leer lassen entfernt den Block.', current);
    if (next === null) return;
    setBusy('changelog'); setOpen(false);
    try {
      await testCockpitApi.setTesterChangelog(runId, next.trim() || null);
      flash(next.trim() ? 'Changelog gespeichert' : 'Changelog entfernt');
      onChanged();
    } catch (e) { alert(`Speichern fehlgeschlagen: ${(e as Error).message}`); }
    finally { setBusy(null); }
  }

  async function blockApprove() {
    if (!runId) return;
    setBusy('approve'); setOpen(false);
    try {
      // Erst Vorschau holen
      const preview = await testCockpitApi.blockApprovePreview(runId);
      if (preview.candidate_count === 0) {
        flash('Keine abnickbaren Funde — alle haben Fehler oder noch kein passt');
        return;
      }
      const titlesPreview = preview.titles.slice(0, 5).map(t => `• ${t}`).join('\n');
      const more = preview.titles.length > 5 ? `\n... und ${preview.titles.length - 5} weitere` : '';
      if (!confirm(`${preview.candidate_count} Funde auf "final" setzen?\n\nKein Tester hat "Fehler" geklickt, mindestens einer "passt".\n\n${titlesPreview}${more}`)) return;
      const r = await testCockpitApi.blockApprove(runId, `Block-Bestaetigung Run ${run?.name ?? ''}`.trim());
      flash(`${r.approved_count} Funde abgenickt, ${r.master_finalized_count} Master auf final`);
      onChanged();
    } catch (e) { alert(`Block-Approve fehlgeschlagen: ${(e as Error).message}`); }
    finally { setBusy(null); }
  }

  async function copyTriageSummary() {
    if (!runId) return;
    setBusy('triage-copy'); setOpen(false);
    try {
      const r = await testCockpitApi.triageSummary(runId);
      if (r.finding_count === 0) {
        flash('Keine freigegebenen Funde — nichts zu kopieren');
        return;
      }
      try {
        await navigator.clipboard.writeText(r.markdown);
        flash(`${r.finding_count} Funde · in Zwischenablage`);
      } catch {
        // Clipboard fehlt → zeig den Text in einem Modal-Substitut
        const w = window.open('', '_blank');
        if (w) {
          w.document.write(`<pre style="white-space:pre-wrap;font:13px monospace;padding:20px;">${r.markdown.replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c] as string))}</pre>`);
          w.document.title = 'Triage-Anweisungen';
        }
        flash(`${r.finding_count} Funde · in neuem Tab geöffnet (Clipboard nicht verfügbar)`);
      }
    } catch (e) {
      alert(`Triage-Zusammenfassung fehlgeschlagen: ${(e as Error).message}`);
    } finally { setBusy(null); }
  }

  async function cleanupScreenshots() {
    if (!confirm('Verwaiste Screenshots aus /uploads/test-cockpit/ aelter als 14 Tage loeschen?')) return;
    setBusy('cleanup'); setOpen(false);
    try {
      const r = await testCockpitApi.screenshotsCleanup(14);
      flash(`Screenshots: ${r.deleted} geloescht, ${r.kept} behalten`);
    } catch (e) { alert(`Cleanup fehlgeschlagen: ${(e as Error).message}`); }
    finally { setBusy(null); }
  }

  return (
    <div className="relative">
      {toast && (
        <div className="absolute right-0 top-full mt-2 z-30 bg-brand-500 text-primary-foreground text-xs px-3 py-1.5 rounded-lg whitespace-nowrap shadow-lg">
          {toast}
        </div>
      )}
      <button
        onClick={() => setOpen(o => !o)}
        disabled={busy !== null}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-card border border-default text-sm hover:bg-primary/5 disabled:opacity-50"
      >
        <MoreHorizontal size={16} />
        Aktionen
        {busy && <Loader2 size={14} className="animate-spin" />}
      </button>
      {showStartModal && (
        <RunStartModal
          onClose={() => setShowStartModal(false)}
          onCreated={onChanged}
        />
      )}
      {showCloneModal && run && runId && (
        <RunCloneModal
          sourceRun={run}
          onClose={() => setShowCloneModal(false)}
          onClone={doClone}
        />
      )}
      {open && (
        <>
          <button onClick={() => setOpen(false)} className="fixed inset-0 z-10 cursor-default" aria-label="Menu schliessen" />
          <div className="absolute right-0 mt-1 w-72 bg-card border border-default rounded-lg shadow-lg z-20 py-1 text-sm">
            <button
              onClick={() => { setOpen(false); setShowStartModal(true); }}
              className="w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-primary/5"
              title="Fehlerlisten-MDs hochladen und einen neuen Test-Run anlegen"
            >
              <Upload size={14} /> Neuen Run anlegen
            </button>
            <div className="border-t border-default my-1" />
            <button
              onClick={endRun}
              disabled={!hasRun || isClosed}
              className="w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-primary/5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              title={!hasRun ? 'Keinen Run ausgewählt' : isClosed ? 'Run ist bereits beendet' : 'Run beenden + Testdaten aus Probst-DB entfernen'}
            >
              <Lock size={14} /> Run beenden + Testdaten entfernen
            </button>
            <div className="border-t border-default my-1" />
            <button
              onClick={() => { setOpen(false); setShowCloneModal(true); }}
              disabled={!hasRun}
              className="w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-primary/5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              title={!hasRun ? 'Keinen Run ausgewählt' : `Run klonen (Quelle: ${run?.name ?? ''})`}
            >
              <GitBranch size={14} /> Run klonen...
            </button>
            <div className="border-t border-default my-1" />
            <button
              onClick={generateChangelog}
              disabled={!hasRun}
              className="w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-primary/5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              title={!hasRun ? 'Keinen Run ausgewählt' : 'Tester-Changelog automatisch aus den Findings + letzten Fix-Versionen generieren'}
            >
              <FileText size={14} /> Tester-Changelog generieren
            </button>
            <button
              onClick={editChangelog}
              disabled={!hasRun}
              className="w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-primary/5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              title={!hasRun ? 'Keinen Run ausgewählt' : 'Generierten Changelog manuell editieren oder entfernen'}
            >
              <FileText size={14} /> Tester-Changelog editieren
            </button>
            <button
              onClick={copyTriageSummary}
              disabled={!hasRun}
              className="w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-primary/5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              title={!hasRun ? 'Keinen Run ausgewählt' : 'Triage-Zusammenfassung als Markdown in die Zwischenablage'}
            >
              <ClipboardCopy size={14} /> Triage-Anweisungen kopieren
            </button>
            <button
              onClick={blockApprove}
              disabled={!hasRun}
              className="w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-primary/5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              title={!hasRun ? 'Keinen Run ausgewählt' : 'Alle Funde abnicken bei denen kein Tester "Fehler" geklickt hat (mind. ein "passt")'}
            >
              <CheckCircle2 size={14} /> Alle ohne Fehler abnicken
            </button>
            <div className="border-t border-default my-1" />
            <button onClick={resetBundle} className="w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-primary/5">
              <Database size={14} /> Test-Bundle reseed
            </button>
            <button onClick={cleanupScreenshots} className="w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-primary/5">
              <Trash2 size={14} /> Screenshots aufraeumen
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function AdminFindingCard({ finding, onChange }: { finding: AggregateFinding; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(finding.triage_note ?? '');
  const [expanded, setExpanded] = useState(false);

  async function triage(status: 'release' | 'discard' | 'clarify' | 'open') {
    setBusy(true);
    try {
      await testCockpitApi.triage(finding.id, { triage_status: status, triage_note: note || undefined });
      onChange();
    } finally {
      setBusy(false);
    }
  }

  async function retest() {
    if (finding.results.length === 0) return;
    if (!confirm(`Tester-Resultate fuer "${finding.title}" zuruecksetzen? ${finding.results.length} Eintrag(e) werden geloescht — Tester muessen den Fund neu durchgehen.`)) return;
    setBusy(true);
    try {
      await testCockpitApi.retest(finding.id);
      onChange();
    } finally {
      setBusy(false);
    }
  }

  const counts = finding.results.reduce(
    (acc, r) => { acc[r.status] = (acc[r.status] ?? 0) + 1; return acc; },
    {} as Record<string, number>
  );
  const triageBadge = TRIAGE_BADGE[finding.triage_status];

  return (
    <div className="bg-card border border-default rounded-lg">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-primary/5"
      >
        <ChevronRight size={16} className={`mt-0.5 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        <span className={`text-xs px-2 py-0.5 rounded shrink-0 ${triageBadge.cls}`}>{triageBadge.label}</span>
        <div className="flex-1 min-w-0">
          <div className="font-medium">{finding.title}</div>
          <div className="text-xs text-muted-foreground mt-0.5 flex gap-3">
            <span>{finding.source_file.replace(/^.*\//, '')} · #{finding.idx + 1}</span>
            <span className="flex gap-2">
              {counts.pass ? <span className="text-brand-500">✓ {counts.pass}</span> : null}
              {counts.fail ? <span className="text-rose-500">✗ {counts.fail}</span> : null}
              {counts.skip ? <span className="text-amber-500">— {counts.skip}</span> : null}
              {finding.results.length === 0 ? <span>noch kein Tester-Feedback</span> : null}
            </span>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-default">
          <div>
            <div className="text-xs text-muted-foreground mb-1 mt-3">Funktion / Erwartung</div>
            <div className="whitespace-pre-wrap text-sm bg-background/40 rounded p-3">{finding.feature_text}</div>
          </div>
          {finding.expectation && (
            <div>
              <div className="text-xs text-muted-foreground mb-1">Erwartetes Verhalten</div>
              <div className="whitespace-pre-wrap text-sm">{finding.expectation}</div>
            </div>
          )}

          {finding.test_path && <TestPathBlock testPath={finding.test_path} />}

          <div>
            <div className="text-xs text-muted-foreground mb-2">Tester-Resultate (editierbar)</div>
            <div className="space-y-2">
              {finding.results.length === 0 && <div className="text-sm text-muted-foreground italic">Noch kein Feedback.</div>}
              {finding.results.map((r) => (
                <AdminTesterResultEditor key={r.result_id ?? r.tester_id} result={r} onSaved={onChange} />
              ))}
            </div>
          </div>

          <div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Triage-Notiz (optional, geht mit in die Vault-Datei)"
              className="w-full px-3 py-2 rounded-lg bg-background border border-default text-sm"
            />
          </div>

          <div className="flex gap-2 flex-wrap">
            <button onClick={() => triage('release')} disabled={busy}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-600 text-primary-foreground text-sm font-medium">
              <ThumbsUp size={16} /> Freigeben
            </button>
            <button onClick={() => triage('discard')} disabled={busy}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-sm">
              <ThumbsDown size={16} /> Verwerfen
            </button>
            <button onClick={() => triage('clarify')} disabled={busy}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm">
              <HelpCircle size={16} /> Klären
            </button>
            {finding.triage_status !== 'open' && (
              <button onClick={() => triage('open')} disabled={busy}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-card border border-default text-sm text-muted-foreground hover:text-foreground">
                Triage zurücksetzen
              </button>
            )}
            {finding.results.length > 0 && (
              <button onClick={retest} disabled={busy}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-card border border-default text-sm text-muted-foreground hover:text-foreground"
                title="Tester-Resultate zuruecksetzen, damit Tester den Fund nach Fix erneut durchgehen">
                <RotateCcw size={16} /> Re-Test
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Admin-Sicht eines einzelnen Tester-Results: editierbare Felder fuer Description
 * + Admin-Kommentar + Status. Joerg redigiert die Beschreibung zur klaren Anweisung
 * an Claude bevor er per Triage „freigibt".
 */
function AdminTesterResultEditor({ result, onSaved }: { result: import('../../../lib/testCockpitApi').TesterResult; onSaved: () => void }) {
  const [comment, setComment] = useState(result.comment ?? '');
  const [adminComment, setAdminComment] = useState(result.admin_comment ?? '');
  const [status, setStatus] = useState<'open' | 'pass' | 'fail'>(result.status);
  const [busy, setBusy] = useState(false);
  const [savedToast, setSavedToast] = useState(false);

  // Server-Sync NUR bei Result-Wechsel (id) — gleicher Bug wie in
  // TesterFindingCard: useEffect mit Content-Dependency wuerde den lokalen
  // Tippzustand bei jedem reloadSilent() ueberschreiben.
  useEffect(() => {
    setComment(result.comment ?? '');
    setAdminComment(result.admin_comment ?? '');
    setStatus(result.status);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.result_id]);

  const dirty = comment !== (result.comment ?? '') || adminComment !== (result.admin_comment ?? '') || status !== result.status;

  async function save() {
    if (!result.result_id) {
      alert('Result-ID fehlt — bitte Seite neu laden.');
      return;
    }
    setBusy(true);
    try {
      await testCockpitApi.adminEditResult(result.result_id, { comment, admin_comment: adminComment, status });
      setSavedToast(true);
      setTimeout(() => setSavedToast(false), 2000);
      onSaved();
    } catch (e) {
      alert(`Speichern fehlgeschlagen: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  const badge = STATUS_BADGE[status];
  return (
    <div className="bg-background/40 rounded p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded ${badge.cls}`}>{badge.label}</span>
          <span className="text-sm font-medium">{result.tester_name || result.tester_email}</span>
        </div>
        <span className="text-xs text-muted-foreground">{new Date(result.updated_at).toLocaleString('de-DE')}</span>
      </div>

      <div>
        <div className="text-xs text-muted-foreground mb-1">Fehlerbeschreibung (geht an Claude)</div>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={2}
          className="w-full px-3 py-2 rounded bg-background border border-default text-sm"
          placeholder="(leer)"
        />
      </div>

      <div>
        <div className="text-xs text-muted-foreground mb-1">Hinweis vom Tester an dich</div>
        <textarea
          value={adminComment}
          onChange={(e) => setAdminComment(e.target.value)}
          rows={2}
          className="w-full px-3 py-2 rounded bg-background border border-default text-sm"
          placeholder="(leer)"
        />
      </div>

      {result.screenshot_path && (
        <a href={result.screenshot_path} target="_blank" rel="noopener noreferrer" className="block">
          <img src={result.screenshot_path} alt="Screenshot" className="max-h-32 rounded border border-default" />
        </a>
      )}

      <div className="flex items-center gap-2">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as 'open' | 'pass' | 'fail')}
          className="px-2 py-1 rounded bg-background border border-default text-sm"
        >
          <option value="open">offen</option>
          <option value="pass">passt</option>
          <option value="fail">fail</option>
        </select>
        <button
          onClick={save}
          disabled={busy || !dirty}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm ${dirty ? 'bg-primary text-white hover:bg-primary/90' : 'bg-card border border-default text-muted-foreground'} disabled:opacity-50`}
        >
          {busy && <Loader2 size={12} className="animate-spin" />}
          Speichern
        </button>
        {savedToast && <span className="text-xs text-brand-500">✓ gespeichert</span>}
        {dirty && !savedToast && <span className="text-xs text-amber-500">ungespeichert</span>}
      </div>
    </div>
  );
}

/**
 * Kompakter Markdown-Renderer fuer das Tester-Changelog. Bewusst minimalistisch
 * (keine 3rd-Party-Dep) — unterstuetzt Headings (h1-h4), Listen (-/*), Trennlinien
 * (---), fette Inline-Spans (**text**) und Inline-Code (`text`). YAML-Frontmatter
 * (--- am Anfang) wird abgeschnitten, Code-Bloecke (```) als Pre-Block gerendert.
 */
function MarkdownView({ source }: { source: string }) {
  // YAML-Frontmatter abschneiden
  let body = source;
  if (body.startsWith('---')) {
    const close = body.indexOf('\n---', 3);
    if (close > 0) body = body.slice(close + 4).replace(/^\s*\n/, '');
  }
  const lines = body.split(/\r?\n/);
  const blocks: React.ReactNode[] = [];
  let i = 0;
  let listBuf: string[] | null = null;
  let codeBuf: string[] | null = null;

  function flushList() {
    if (!listBuf) return;
    blocks.push(
      <ul key={`l${blocks.length}`} className="list-disc list-inside space-y-1 ml-2 my-2 text-sm">
        {listBuf.map((it, idx) => <li key={idx}>{renderInline(it)}</li>)}
      </ul>
    );
    listBuf = null;
  }
  function flushCode() {
    if (!codeBuf) return;
    blocks.push(
      <pre key={`c${blocks.length}`} className="bg-slate-950/60 border border-default rounded p-2 my-2 text-xs overflow-x-auto font-mono">
        {codeBuf.join('\n')}
      </pre>
    );
    codeBuf = null;
  }

  while (i < lines.length) {
    const line = lines[i]!;
    if (codeBuf !== null) {
      if (/^```/.test(line)) flushCode();
      else codeBuf.push(line);
      i++; continue;
    }
    if (/^```/.test(line)) { flushList(); codeBuf = []; i++; continue; }
    const heading = line.match(/^(#{1,4})\s+(.+)$/);
    if (heading) {
      flushList();
      const level = heading[1]!.length;
      const text = heading[2]!.trim();
      const cls = level === 1 ? 'text-base font-bold mt-2 mb-1'
        : level === 2 ? 'text-sm font-semibold mt-3 mb-1 text-brand-400'
        : level === 3 ? 'text-sm font-semibold mt-2 mb-0.5'
        : 'text-xs font-semibold uppercase tracking-wide text-muted-foreground mt-2';
      const Tag = `h${Math.min(level + 1, 6)}` as 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
      blocks.push(<Tag key={`h${blocks.length}`} className={cls}>{renderInline(text)}</Tag>);
      i++; continue;
    }
    if (/^---+\s*$/.test(line)) {
      flushList();
      blocks.push(<hr key={`hr${blocks.length}`} className="border-brand-500/20 my-3" />);
      i++; continue;
    }
    const li = line.match(/^\s*[-*]\s+(.+)$/);
    if (li) {
      if (!listBuf) listBuf = [];
      listBuf.push(li[1]!);
      i++; continue;
    }
    if (line.trim() === '') {
      flushList();
      i++; continue;
    }
    flushList();
    blocks.push(<p key={`p${blocks.length}`} className="text-sm my-1.5 leading-relaxed">{renderInline(line)}</p>);
    i++;
  }
  flushList();
  flushCode();
  return <div className="text-foreground">{blocks}</div>;
}

/** Inline-Markdown: **bold**, `code`, sonst plain. */
function renderInline(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) {
      parts.push(<strong key={`b${key++}`} className="font-semibold text-foreground">{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('`')) {
      parts.push(<code key={`c${key++}`} className="font-mono text-xs px-1 py-0.5 rounded bg-slate-950/40 border border-default">{tok.slice(1, -1)}</code>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
